import { FC, useMemo } from 'react';

import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Language } from '../../../services/contentful/locale.service';
import { DataTestSelector } from '../../../utils/format.utils';

interface Props {
  languages: Array<Language>;
  selectedLanguageCode: string;
  onLanguageSelect?: (languageCode: string) => void;
}

const LanguageSwitch: FC<Props> = ({ languages, selectedLanguageCode, onLanguageSelect }: Props) => {
  const translate = useTranslations();

  const languageOptions = useMemo<DropdownOption[]>(
    () =>
      languages
        .map((language) => ({
          id: language.shortName,
          value: language.fullName
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [languages]
  );

  const selectedLanguageOption = languageOptions.find((option) => option.id === selectedLanguageCode);

  const handleLanguageSelect = (languageOption?: DropdownOption) => {
    if (!languageOption) return;
    onLanguageSelect?.(languageOption?.id);
  };

  return (
    <Box direction="column" gap="sm">
      <Typography variant="body-highlight" color="strong">
        {translate('page-consumer.header.language-selector.header')}
      </Typography>

      <Dropdown id="language-selector" attributes={{ ...DataTestSelector('language-switch-dropdown') }} emptyOption={false} label={translate('page-consumer.header.language-selector.label')} value={selectedLanguageOption} options={languageOptions} onChange={handleLanguageSelect} />
    </Box>
  );
};

export default LanguageSwitch;
